import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import axios from 'axios';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';
import qs from 'qs';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  BadgeAlt,
} from '@cargomatic/ui';
import {
  ChevronUp,
  Download,
  TriangleAlert,
  CircleAlert,
  Info,
  CheckCircle,
  X,
  Filter,
  Lock,
  RotateCcw,
  ArrowUpDown,
} from 'lucide-react';
import useInterval from 'hooks/useInterval';
import SessionStore from 'stores/SessionStore';
import Container from 'components/v2/container';
import CargomaticModal from 'components/v2/modal';
import CargomaticInput from 'components/v2/input';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import {
  ADD_POD,
  ADD_DOCUMENTS,
  COMPLETE_STOP,
  CONFIRM_APPT,
  SCHEDULE_APPT,
  SORTING_OPTIONS,
  ENTER_REQUIRED_DATES,
  TABS,
} from 'constants/next-actions';
import { useHistory, useLocation } from 'react-router-dom';
import LegacyAlert from 'components/ui/Alert';
import {
  getNextActions,
  getLFDAlertAction,
  getEmptyPickupReadyAction,
  triggerExportCSV,
  getMissingPodAction,
} from 'utils/ShipmentWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import { getUserContextGrants } from 'utils/UserWebAPIUtils';
import LoadingCircle from 'components/ui/LoadingCircle';
import ShipmentClosedModal from 'components/ui/modals/ShipmentClosedModal';
import RatingEngineModal from 'components/ui/modals/RatingEngineModal';
import { sluggify } from 'utils/localization/utils';
import { unescapeSpecialChars } from 'src/main/js/helpers/helper_functions';
import { FilterBarRegistry } from 'components/design-system/molecules/FilterBarRegistry';
import {
  useFilterState,
  initialValuesFromConfigs,
} from 'components/design-system/hooks/useFilterState';
import { buildQuery } from 'components/design-system/utils/buildQuery';
import NextActionsList from './NextActionsList';
import usePageVisibility from '../../../hooks/usePageVisibility';
import { makeCarrierDashboardFilters } from './CarrierDashboard.filters';

const REQUIREMENT_TAB = !featureEnabled(CONFIGURED_FEATURES.SSR)
  ? ADD_POD
  : ADD_DOCUMENTS;

const DEFAULT_PER_PAGE = 10;

const REFRESH_INTERVAL = 120000;

function useStateCallback(initialState) {
  const [state, setState] = useState(initialState);
  const cbRef = useRef(null);

  const setStateCallback = useCallback((nextState, cb) => {
    cbRef.current = cb;
    setState(nextState);
  }, []);

  useEffect(() => {
    if (cbRef.current) {
      cbRef.current(state);
      cbRef.current = null;
    }
  }, [state]);

  return [state, setStateCallback];
}

export default function CarrierDashboard() {
  const { t } = useTranslation();
  const history = useHistory();
  const thislocation = useLocation();
  const { search } = thislocation;
  const isAdmin = SessionStore.isAdmin();
  const isCarrier = SessionStore.isCarrier();

  const [locationParsed, setLocationParsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionType, setActionType] = useState(COMPLETE_STOP);
  const [actions, setActions] = useState({
    [REQUIREMENT_TAB]: [],
    [COMPLETE_STOP]: [],
    [CONFIRM_APPT]: [],
    [ENTER_REQUIRED_DATES]: [],
    [SCHEDULE_APPT]: [],
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ldfAlertsTotal, setLfdAlertsTotal] = useState(0);
  const [emptyPickupTotal, setEmptyPickupTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [pages, setPages] = useState({
    [REQUIREMENT_TAB]: 1,
    [COMPLETE_STOP]: 1,
    [CONFIRM_APPT]: 1,
    [ENTER_REQUIRED_DATES]: 1,
    [SCHEDULE_APPT]: 1,
  });
  const [total, setTotal] = useState({
    [REQUIREMENT_TAB]: 0,
    [COMPLETE_STOP]: 0,
    [CONFIRM_APPT]: 0,
    [ENTER_REQUIRED_DATES]: 0,
    [SCHEDULE_APPT]: 0,
  });
  const [sortValue, setSortValue] = useState(SORTING_OPTIONS[0].value);
  const [scopes, setScopes] = useStateCallback(null);
  const [cancelTokenSource, setCancelTokenSource] = useState();
  const [showTotal, setShowTotal] = useState(true);
  const [showEmailRecipientsModal, setShowEmailRecipientsModal] =
    useState(false);
  const [emailRecipientsForCSV, setEmailRecipientsForCSV] = useState('');
  const [tabsWithCounts, setTabsWithCounts] = useState(TABS);
  const [closedShipment, setClosedShipment] = useState();
  const [selectedAction, setSelectedAction] = useState();
  const [shipmentUpdatedOnModalClose, setShipmentUpdatedOnModalClose] =
    useState(false);

  const pathIncludesModalVal = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  const [ratingEngineModal, setRatingEngineModal] = useState(
    !!pathIncludesModalVal.confirmLaneRates ||
      !!pathIncludesModalVal.confirmAccessorialRates,
  );
  const [ratingEngineModalType] = useState(
    pathIncludesModalVal.confirmLaneRates === 'true' ||
      pathIncludesModalVal.confirmAccessorialRates === 'true',
  );
  const [ratingEngineModalRateType] = useState(
    pathIncludesModalVal.confirmLaneRates === 'true' ||
      pathIncludesModalVal.confirmLaneRates === 'false'
      ? 'LaneRates'
      : 'LaneAccessorialRates',
  );
  const [ratingEngineModalFrom] = useState(
    pathIncludesModalVal.confirmLaneRates ? '' : 'Accessorial',
  );
  const [ratingEngineModalToken] = useState(
    pathIncludesModalVal.token ? pathIncludesModalVal.token : '',
  );

  // --- Filter state via design-system plug-and-play -----------------------
  const FILTERS = useMemo(
    () => makeCarrierDashboardFilters({ isAdmin }),
    [isAdmin],
  );
  const initialValues = useMemo(
    () => initialValuesFromConfigs(FILTERS),
    [FILTERS],
  );
  const { values, displayed, labels, setValue, setMany, setLabel, toggle } =
    useFilterState(initialValues);

  const filterQuery = useMemo(
    () => buildQuery(FILTERS, values),
    [FILTERS, values],
  );

  const lockedCarrierId = !isAdmin ? SessionStore.getUser()?._id : null;
  const lockedCarrierName = !isAdmin ? SessionStore.getUser()?.name : null;

  const apiFilters = useMemo(() => {
    const dr = filterQuery.date_range || {};
    const carrier = lockedCarrierId
      ? [lockedCarrierId, ...(filterQuery.carrier || [])]
      : filterQuery.carrier || [];
    return {
      carrier,
      shipper: filterQuery.shipper || [],
      shipment: filterQuery.shipment || [],
      start_date: dr.start_date,
      end_date: dr.end_date,
      date_type: dr.date_type || 'pickup',
    };
  }, [filterQuery, lockedCarrierId]);

  function cancelRequest() {
    if (cancelTokenSource) {
      cancelTokenSource.cancel();
    }
  }

  useEffect(() => {
    return () => {
      cancelRequest();
    };
  }, []);

  const isVisible = usePageVisibility(true);

  useInterval(fetchNextActions, REFRESH_INTERVAL);

  useEffect(() => {
    setFiltersFromQuery();
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (locationParsed) {
      fetchLfdData();
      fetchEmptyPickupReady();
    }
  }, [locationParsed, actionType, apiFilters]);

  useEffect(() => {
    if (locationParsed) {
      fetchNextActions();
    }
  }, [locationParsed, page, perPage, sortValue, apiFilters]);

  useEffect(() => {
    document.title = t('CARRIER_DASHBOARD_TITLE');
  }, []);

  useEffect(() => {
    async function retrieveScopes() {
      const shipmentScopes = await getUserContextGrants(['shipments']);
      if (shipmentScopes) {
        const nextScopes = shipmentScopes?.entity?.scopes?.shipments;
        if (nextScopes) {
          setScopes(nextScopes);
        }
      }
    }
    if (isCarrier && !scopes) {
      retrieveScopes();
    }
  });

  useEffect(() => {
    if (
      selectedAction === null &&
      !isModalOpen &&
      shipmentUpdatedOnModalClose
    ) {
      handleReloadActions();
    }
  }, [selectedAction, isModalOpen, shipmentUpdatedOnModalClose]);

  async function setFiltersFromQuery() {
    const query = qs.parse(thislocation.search, { ignoreQueryPrefix: true });
    if (query.action_type && query.action_type.length > 0) {
      setActionType(query.action_type?.toUpperCase() || COMPLETE_STOP);
    }
    if (query.page && query.page.length > 0) {
      setPage(parseInt(query.page, 10) || 1);
    }
    if (
      query.sort &&
      query.sort.length > 0 &&
      query.sort_dir &&
      query.sort_dir.length > 0
    ) {
      setSortValue(
        [query.sort, query.sort_dir].join('__') || SORTING_OPTIONS[0].value,
      );
    }
    setLocationParsed(true);
  }

  function translateFilterMetadata(currentFilters) {
    return {
      carrier: currentFilters.carrier || [],
      shipper: currentFilters.shipper || [],
      shipment: currentFilters.shipment || [],
      date_type: currentFilters.date_type,
      start_date: currentFilters.start_date,
      end_date: currentFilters.end_date,
    };
  }

  async function fetchNextActions() {
    if (!isVisible || isModalOpen) {
      return;
    }
    try {
      setLoading(true);
      if (!isCarrier && !isAdmin) {
        return;
      }
      const [sort, sort_dir] = sortValue.split('__');

      if (cancelTokenSource) {
        cancelRequest();
      }

      const source = axios.CancelToken.source();
      const cancelToken = source.token;
      setCancelTokenSource(source);

      const results = await getNextActions({
        actionType,
        per_page: perPage,
        page,
        sort,
        sort_dir,
        filterMetadata: translateFilterMetadata(apiFilters),
        cancelToken,
      });
      setPages({
        [REQUIREMENT_TAB]: results[REQUIREMENT_TAB].pages,
        [COMPLETE_STOP]: results[COMPLETE_STOP].pages,
        [CONFIRM_APPT]: results[CONFIRM_APPT].pages,
        [ENTER_REQUIRED_DATES]: results[ENTER_REQUIRED_DATES].pages,
        [SCHEDULE_APPT]: results[SCHEDULE_APPT].pages,
      });
      setTotal({
        [REQUIREMENT_TAB]: results[REQUIREMENT_TAB].total,
        [COMPLETE_STOP]: results[COMPLETE_STOP].total,
        [CONFIRM_APPT]: results[CONFIRM_APPT].total,
        [ENTER_REQUIRED_DATES]: results[ENTER_REQUIRED_DATES].total,
        [SCHEDULE_APPT]: results[SCHEDULE_APPT].total,
      });
      setActions({
        [REQUIREMENT_TAB]: results[REQUIREMENT_TAB].actions,
        [COMPLETE_STOP]: results[COMPLETE_STOP].actions,
        [CONFIRM_APPT]: results[CONFIRM_APPT].actions.map((a) => ({
          ...a,
          nextStop: {
            ...a.nextStop,
            action: t('CONFIRM_APPT_ACTION', {
              nextStop_action: t(sluggify(a.nextStop.action)),
            }),
          },
        })),
        [ENTER_REQUIRED_DATES]: results[ENTER_REQUIRED_DATES].actions,
        [SCHEDULE_APPT]: results[SCHEDULE_APPT].actions.map((a) => ({
          ...a,
          nextStop: {
            ...a.nextStop,
            action: t('SCHEDULE_APPT_ACTION', {
              nextStop_action: t(sluggify(a.nextStop.action)),
            }),
          },
        })),
      });
      setTabsWithCounts((prevTabs) =>
        prevTabs.map((tab) => ({ ...tab, count: results[tab.name].total })),
      );
    } catch (_err) {
      showToast(
        t('FETCH_NEXT_ACTIONS_FAILED'),
        t('FETCH_NEXT_ACTIONS_FAILED_MESSAGE'),
        'error',
      );
    } finally {
      setLoading(false);
      setCancelTokenSource(null);
    }
  }

  async function fetchLfdData() {
    try {
      const {
        meta: { total: lfdTotal },
      } = await getLFDAlertAction(translateFilterMetadata(apiFilters));
      setLfdAlertsTotal(lfdTotal);
    } catch (err) {
      console.error(err);
      showToast(t('FETCH_LFD_FAILED'), t('FETCH_LFD_FAILED_MESSAGE'), 'error');
    }
  }

  async function fetchEmptyPickupReady() {
    try {
      const {
        meta: { total: empTotal },
      } = await getEmptyPickupReadyAction(translateFilterMetadata(apiFilters));
      setEmptyPickupTotal(empTotal);
    } catch (e) {
      console.error(e);
      showToast(
        t('FETCH_EMPTY_PICKUP_FAILED'),
        t('FETCH_EMPTY_PICKUP_FAILED'),
        'error',
      );
    }
  }

  function handleReloadActions() {
    setTimeout(() => {
      fetchNextActions();
      setShipmentUpdatedOnModalClose(false);
    }, 0);
  }

  function handlePageClick(selectedPage) {
    setPage(selectedPage);
  }

  function handlePerPageChange(nextPerPage) {
    setPerPage(nextPerPage);
    setPage(1);
  }

  function handleTabChange(selectedActionType) {
    setActionType(selectedActionType);
    setPage(1);
  }

  function handleFilterChange(id, value) {
    setValue(id, value);
    setPage(1);
  }

  function handleFilterToggle(id) {
    toggle(id);
    setPage(1);
  }

  function handleResetFilters() {
    const cleared = {};
    for (const cfg of FILTERS) {
      cleared[cfg.id] =
        cfg.kind === 'select-multi' || cfg.kind === 'autocomplete-multi'
          ? []
          : cfg.kind === 'toggle'
            ? false
            : cfg.initialValue !== undefined
              ? cfg.initialValue
              : null;
    }
    setMany(cleared);
    setPage(1);
  }

  async function setSelectedActionShipment(selectedShipment) {
    const isShorthaulWithMissingPOD =
      selectedShipment?.shipment?.shipment_type === 'shorthaul' &&
      [ADD_DOCUMENTS, ADD_POD].includes(selectedShipment?.action);

    if (isShorthaulWithMissingPOD) {
      const shipmentId = selectedShipment?.shipment?._id;
      const {
        data: { data = {} },
      } = await getMissingPodAction({ shipment: shipmentId });

      const shipment = data?.actions?.length
        ? data.actions[0]
        : selectedShipment;
      setSelectedAction(shipment);
    } else {
      setSelectedAction(selectedShipment);
    }
  }

  async function handleExportToCSV() {
    try {
      const [sort, sort_dir] = sortValue.split('__');
      const emailRecipients = emailRecipientsForCSV
        .split(',')
        .map((item) => item.trim());
      await triggerExportCSV(
        {
          sort,
          sort_dir,
          ...translateFilterMetadata(apiFilters),
        },
        emailRecipients,
      );
      showToast(t('EXPORT_CSV'), t('CSV_EXPORT_REQUEST_SUCCESS'), 'success');
      handleCloseEmailRecipientsModal();
    } catch (_err) {
      showToast(t('EXPORT_CSV'), t('CSV_EXPORT_REQUEST_FAILED'), 'error');
      handleCloseEmailRecipientsModal();
    }
  }

  function handleShowEmailRecipientsModal() {
    setShowEmailRecipientsModal(true);
  }

  function handleCloseEmailRecipientsModal() {
    setEmailRecipientsForCSV('');
    setShowEmailRecipientsModal(false);
  }

  function renderNotificationBand({ severity, icon, children, onDismiss }) {
    const tone = {
      error: {
        wrap: 'border-l-4 border-red-500 bg-red-50',
        text: 'text-red-800',
        iconColor: 'text-red-600',
      },
      warning: {
        wrap: 'border-l-4 border-yellow-500 bg-yellow-50',
        text: 'text-yellow-800',
        iconColor: 'text-yellow-600',
      },
      notice: {
        wrap: 'border-l-4 border-blue-500 bg-blue-50',
        text: 'text-blue-800',
        iconColor: 'text-blue-600',
      },
    }[severity];
    return (
      <div className={`flex items-center gap-3 px-4 py-3 ${tone.wrap}`}>
        <span className={`shrink-0 flex items-center ${tone.iconColor}`}>
          {icon}
        </span>
        <span className={`flex-1 min-w-0 text-sm leading-5 ${tone.text}`}>
          {children}
        </span>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className={`shrink-0 self-center rounded p-1 hover:bg-black/5 ${tone.iconColor}`}
          >
            <X className="size-4" />
          </button>
        )}
      </div>
    );
  }

  function renderAlertBanner() {
    const showCompleteStopReminder =
      total[COMPLETE_STOP] > 0 && total[REQUIREMENT_TAB] > 0;
    const showNotice = total[actionType] > 0 && showTotal;

    const errorCount = ldfAlertsTotal > 0 ? 1 : 0;
    const warningCount = emptyPickupTotal > 0 ? 1 : 0;
    const noticeCount = showNotice ? 1 : 0;
    const totalCount = errorCount + warningCount + noticeCount;

    if (totalCount === 0 && !showCompleteStopReminder) {
      return null;
    }

    return (
      <div className="w-full mx-auto mb-4 space-y-3">
        <Collapsible defaultOpen className="border rounded-md bg-background">
          <CollapsibleTrigger className="group flex items-center justify-between w-full px-4 py-3 text-left">
            <div className="flex items-center gap-5 text-sm">
              {errorCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                  <span className="font-medium text-red-600">
                    {errorCount} {t('error', { defaultValue: 'error' })}
                  </span>
                </span>
              )}
              {warningCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-yellow-500" />
                  <span className="font-medium text-yellow-600">
                    {warningCount} {t('warning', { defaultValue: 'warning' })}
                  </span>
                </span>
              )}
              {noticeCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-blue-500" />
                  <span className="font-medium text-blue-600">
                    {noticeCount} {t('notice', { defaultValue: 'notice' })}
                  </span>
                </span>
              )}
              <span className="text-muted-foreground">
                {totalCount}{' '}
                {t('notifications', { defaultValue: 'notifications' })} —{' '}
                <span className="group-data-[state=open]:hidden">
                  {t('click_to_show', { defaultValue: 'click to show' })}
                </span>
                <span className="group-data-[state=closed]:hidden">
                  {t('click_to_hide', { defaultValue: 'click to hide' })}
                </span>
              </span>
            </div>
            <ChevronUp className="size-4 transition-transform group-data-[state=closed]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent className="border-t">
            {errorCount > 0 &&
              renderNotificationBand({
                severity: 'error',
                icon: <CircleAlert className="size-4" />,
                children: (
                  <Trans
                    i18nKey="SHIPMENTS_PAST_LFD_WITH_COUNT"
                    values={{ num: ldfAlertsTotal }}
                    components={[<span key="0" className="font-bold" />]}
                  />
                ),
              })}
            {warningCount > 0 &&
              renderNotificationBand({
                severity: 'warning',
                icon: <TriangleAlert className="size-4" />,
                children: (
                  <Trans
                    i18nKey="SHIPMENTS_EMPTY_PICKUP_WITH_COUNT"
                    values={{ num: emptyPickupTotal }}
                    components={[<span key="0" className="font-bold" />]}
                  />
                ),
              })}
            {showNotice &&
              renderNotificationBand({
                severity: 'notice',
                icon: <Info className="size-4" />,
                onDismiss: () => setShowTotal(false),
                children: (
                  <Trans
                    i18nKey="SHIPMENTS_REQUIRE_ATTENTION"
                    values={{ num: total[actionType] }}
                    components={[<span key="0" className="font-bold" />]}
                  />
                ),
              })}
          </CollapsibleContent>
        </Collapsible>
        {showCompleteStopReminder && (
          <Alert variant="default">
            <CheckCircle className="size-4 text-green-600" />
            <AlertDescription>
              {t('COMPLETE_SHIPMENTS_REMINDER')}
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  function renderTabs() {
    return (
      <Tabs
        value={actionType}
        onValueChange={handleTabChange}
        className="px-4 pt-4"
      >
        <TabsList className="flex flex-wrap h-auto justify-start bg-transparent p-0 gap-x-8 gap-y-2">
          {tabsWithCounts.map((tab) => (
            <TabsTrigger
              key={tab.name}
              value={tab.name}
              className={classnames(
                'gap-2 px-3 py-2 data-[state=active]:bg-primary/10 data-[state=active]:text-primary',
              )}
            >
              <span>{t(tab.name)}</span>
              {tab.count ? (
                <BadgeAlt
                  variant="secondary"
                  className="ml-1 h-5 px-1.5 text-xs font-medium"
                >
                  {tab.count}
                </BadgeAlt>
              ) : null}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    );
  }

  function renderFilterRow() {
    return (
      <div className="flex items-end bg-muted/50 rounded-md py-2 px-3 gap-2 flex-wrap">
        {!isAdmin && lockedCarrierName && (
          <BadgeAlt
            variant="secondary"
            className="h-8 px-2 gap-1.5 flex items-center text-xs font-medium border bg-background"
          >
            <Lock className="size-3" />
            {unescapeSpecialChars(lockedCarrierName)}
          </BadgeAlt>
        )}
        <div className="flex items-end gap-2 flex-1 min-w-0 flex-wrap">
          <FilterBarRegistry
            configs={FILTERS}
            values={values}
            labels={labels}
            displayed={displayed}
            onChange={handleFilterChange}
            onLabelChange={setLabel}
            onToggle={handleFilterToggle}
          />
          <Select value={sortValue} onValueChange={setSortValue}>
            <SelectTrigger
              size="sm"
              className="h-8 w-48 border-dashed bg-background"
            >
              <ArrowUpDown className="size-3 mr-1.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORTING_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {t(option.name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="shrink-0 ml-2 pl-2 border-l flex items-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleShowEmailRecipientsModal}
            className="h-8 px-2 gap-1.5 text-xs whitespace-nowrap"
          >
            <Download className="size-3" />
            {t('EXPORT_CSV')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetFilters}
            className="h-8 px-2 gap-1.5 text-xs whitespace-nowrap"
          >
            <RotateCcw className="size-3" />
            Reset
          </Button>
        </div>
      </div>
    );
  }

  function getEmailModalBody() {
    return (
      <div className="mb-4">
        <CargomaticInput
          type="textarea"
          rows={3}
          label={t('EMAIL_RECIPIENTS')}
          placeholder={t('ADD_EMAIL_WITH_COMMA')}
          value={emailRecipientsForCSV}
          onChange={(emails) => setEmailRecipientsForCSV(emails)}
        />
      </div>
    );
  }

  function getEmailModalFooter() {
    return (
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={handleCloseEmailRecipientsModal}>
          {t('CANCEL')}
        </Button>
        <Button
          onClick={handleExportToCSV}
          disabled={!emailRecipientsForCSV.length}
        >
          {t('CONFIRM')}
        </Button>
      </div>
    );
  }

  function renderCSVEmailRecipientsModal() {
    return (
      <CargomaticModal
        title={t('EXPORT_CSV_FILE')}
        style={{ top: '30%' }}
        show={showEmailRecipientsModal}
        onHide={handleCloseEmailRecipientsModal}
        body={getEmailModalBody()}
        footer={getEmailModalFooter()}
      />
    );
  }

  if (!isAdmin && !isCarrier) {
    return (
      <div className="p-4">
        <LegacyAlert
          title={t('REQUIRE_PERMISSION_TO_VIEW_PAGE')}
          type="error"
        />
      </div>
    );
  }

  return (
    <>
      <Container className="min-h-screen py-10">
        {isCarrier && (
          <h2 className="my-0 mb-4">
            <Trans
              i18nKey="HELLO_CARRIER"
              values={{
                carrier_name: unescapeSpecialChars(SessionStore.getUser().name),
              }}
              components={[<span key="0" className="font-normal" />]}
            />
          </h2>
        )}
        {renderAlertBanner()}

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-semibold m-0">
                {t('PENDING_TASKS', { defaultValue: 'Pending Tasks' })}
              </h2>
              <Button
                variant={showFilters ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setShowFilters((prev) => !prev)}
                className="h-8 w-8 p-0"
                aria-label="Toggle filters"
                aria-expanded={showFilters}
              >
                <Filter className="size-4" />
              </Button>
            </div>
            {showFilters && renderFilterRow()}
          </CardHeader>
          {renderTabs()}
          <CardContent className="px-4 pb-4 pt-4">
            <NextActionsList
              actions={actions[actionType]}
              actionType={actionType}
              isCustomerView={isCarrier}
              onPageClick={handlePageClick}
              onPerPageChange={handlePerPageChange}
              perPage={perPage}
              page={page}
              pages={pages[actionType]}
              total={total[actionType]}
              loading={loading}
              setIsModalOpen={setIsModalOpen}
              onShipmentClosed={setClosedShipment}
              selectedAction={selectedAction}
              setSelectedAction={setSelectedActionShipment}
              setShipmentUpdatedOnModalClose={setShipmentUpdatedOnModalClose}
            />
            {loading && !actions[actionType]?.length && (
              <div className="p-4">
                <LoadingCircle text={t('LOADING')} />
              </div>
            )}
          </CardContent>
        </Card>

        {closedShipment && (
          <ShipmentClosedModal
            show={closedShipment}
            onAccessorialClick={() => {
              history.push(
                '/shipments/' + closedShipment._id + '?action=payout',
              );
            }}
            onCancelClick={() => setClosedShipment()}
          />
        )}
        {ratingEngineModal && (
          <RatingEngineModal
            show={ratingEngineModal}
            onCloseClick={() => setRatingEngineModal()}
            from={ratingEngineModalFrom}
            rateType={ratingEngineModalRateType}
            type={ratingEngineModalType}
            token={ratingEngineModalToken}
          />
        )}
      </Container>
      {renderCSVEmailRecipientsModal()}
    </>
  );
}
